import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import GoogleSignIn from "../components/GoogleSignIn";
import { API_BASE, setSession, refreshAccessToken } from "../utils/auth";

export interface LoginResponse {
  status: string;
  token: string;
  refresh_token: string;
}

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const tryRefreshToken = async () => {
      // Restore a session from the HttpOnly refresh cookie (or migrate a legacy
      // localStorage session). Only redirect if a session actually restores.
      const token = await refreshAccessToken();
      if (token) navigate("/uploads");
    };

    tryRefreshToken();
  }, [navigate]);

  const loginUser = async (email: string, password: string) => {
    const response = await fetch(`${API_BASE}/login`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Auth-Mode": "cookie",
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      throw new Error("Login failed");
    }

    const data: LoginResponse = await response.json();
    if (data.status !== "ok") {
      navigate("/subscribe");
      throw new Error("User not subscribed");
    }
    return data;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await loginUser(email, password);
      setSession(res.token);
      setError("");
      navigate("/uploads");
    } catch (err) {
      setError("Login failed. Check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = (data: {
    status: string;
    token: string;
    refresh_token: string;
    [key: string]: unknown;
  }) => {
    if (data.status !== "ok") {
      setError(
        "Account pending activation. Please contact your administrator.",
      );
      return;
    }

    setSession(data.token);
    setError("");
    navigate("/uploads");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0b0f] bg-grid-pattern relative overflow-hidden px-4">
      {/* Radial gradient overlay */}
      <div className="absolute inset-0 bg-radial-gradient pointer-events-none" />

      {/* Decorative elements */}
      <div className="absolute top-20 left-20 w-64 h-64 bg-amber-400/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-amber-400/3 rounded-full blur-3xl" />

      {/* Login card */}
      <div className="relative w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8 opacity-0 animate-slide-up stagger-1">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 mb-4 shadow-lg shadow-amber-500/20">
            <svg
              className="w-8 h-8 text-[#0a0b0f]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-semibold text-zinc-100 tracking-tight">
            Adjuster Photos
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Sign in to access your photo library
          </p>
        </div>

        {/* Form card */}
        <div className="card-dark rounded-2xl p-8 shadow-2xl shadow-black/50 opacity-0 animate-slide-up stagger-2">
          {/* Google Sign-In — Primary */}
          <div className="mb-4 opacity-0 animate-slide-up stagger-3">
            <GoogleSignIn
              onSuccess={handleGoogleSuccess}
              onError={(msg) => setError(msg)}
              disabled={isLoading}
            />
          </div>

          <p className="text-center text-zinc-500 text-xs mb-4">
            Sign in or create your account instantly with Google
          </p>

          {/* Error message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm text-center mb-4">
              {error}
            </div>
          )}

          {/* Email/password — collapsed by default */}
          {!showEmailForm ? (
            <div className="text-center pt-2 border-t border-zinc-700/30">
              <button
                onClick={() => setShowEmailForm(true)}
                className="text-zinc-500 hover:text-zinc-300 text-sm transition-colors mt-3"
              >
                Sign in with email and password instead
              </button>
            </div>
          ) : (
            <>
              {/* Divider */}
              <div className="relative mb-5 mt-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-zinc-700/50" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 text-zinc-500 bg-[var(--color-bg-card)]">
                    or sign in with email
                  </span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-zinc-400 mb-2"
                  >
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input-dark w-full px-4 py-3 rounded-xl text-zinc-100"
                  />
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-zinc-400 mb-2"
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="input-dark w-full px-4 py-3 rounded-xl text-zinc-100"
                  />

                  <div className="mt-3 flex justify-between text-sm">
                    <a href="/#forgot-password" className="link-muted hover:underline">
                      Forgot password?
                    </a>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary w-full py-3.5 rounded-xl text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isLoading ? (
                      <span className="inline-flex items-center gap-2">
                        <svg
                          className="animate-spin h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Signing in...
                      </span>
                    ) : (
                      "Sign in"
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-zinc-600 text-sm mt-6 opacity-0 animate-fade-in stagger-5">
          Secure photo management for insurance professionals
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
